/**
 * Responsibility: one holding in full.
 *
 * The same dialog as the search and discovery ones - same frame, same size,
 * same tabs, same rule that it never scrolls - built on `CardDialogShell`.
 * The one deliberate difference is the third tab: a holding has no eBay
 * listings worth showing, so that slot carries the details only the
 * operator knows instead.
 *
 * Those details exist because nothing upstream can supply them. A
 * certificate number names this exact copy rather than the card; a purchase
 * date is not the date the row was created, and a gain measured from the
 * wrong date is measured over the wrong window; a cost basis is the only
 * thing that turns a value into a profit.
 *
 * Saving sends only what changed, and blanking a field is an explicit
 * instruction rather than an omission - a form that wipes whatever it did
 * not ask about loses data quietly.
 */
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import LaunchIcon from '@mui/icons-material/Launch';
import type { CollectionItem, CollectionTag, EditHolding } from '../../api/client';
import { useMarketCard } from '../../api/hooks/useMarket';
import { usePageState } from '../../hooks/usePageState';
import { CardChartArea } from '../cards/CardChartArea';
import { CardDialogShell } from '../cards/CardDialogShell';
import { CardSalesTab } from '../cards/CardSalesTab';
import { Figure } from '../grading/CardFigures';
import { money, toNumber } from '../grading/money';
import { fromHolding } from '../shelf/shelfCard';
import { CollectionTile } from './CollectionTile';
import { HoldingTagPicker } from './HoldingTagPicker';
import { HoldingDetailsForm } from './HoldingDetailsForm';
import type { HoldingForm } from './HoldingDetailsForm';

/** Matches the card image on the other two dialogs. */
const TILE_WIDTH = 150;

type HoldingDialogProps = {
  item: CollectionItem | null;
  /** Sub-collections that exist, so the card can be put in them. */
  tags: CollectionTag[];
  busy: boolean;
  onClose: () => void;
  onSave: (itemId: string, changes: EditHolding) => void;
};

export function HoldingDialog({ item, tags, busy, onClose, onSave }: HoldingDialogProps) {
  const [tab, setTab] = usePageState('collection.dialog.tab', 0);
  // One object rather than seven pieces of state: the form is reloaded
  // and read back as a unit, and seven setters meant seven places to
  // forget when a field was added.
  const [form, setForm] = useState<HoldingForm>({
    company: '',
    grade: '',
    cert: '',
    acquired: '',
    cost: '',
    quantity: '1',
    notes: '',
  });

  // The catalogue's view of this card, so the figures and the sale history
  // match what the other two dialogs show for it.
  const detail = useMarketCard(item?.product_key ?? null);

  // Reloaded whenever a different holding is opened, so the form never
  // shows one card's details under another's name.
  useEffect(() => {
    if (!item) return;
    setForm({
      company: item.grading_company ?? '',
      grade: item.grade_label,
      cert: item.cert_number ?? '',
      acquired: item.acquired_on ?? '',
      cost: item.cost_basis ?? '',
      quantity: String(item.quantity),
      notes: item.notes,
    });
  }, [item]);

  if (!item) return null;

  const card = detail.data?.result ?? null;
  const sales = detail.data?.sales ?? [];
  const raw = form.company === '';
  const gain = toNumber(item.gain);

  const save = () => {
    const count = Number(form.quantity);
    // Anything the operator emptied is named explicitly, so the server can
    // tell "clear this" from "I did not touch it".
    const cleared: string[] = [];
    if (form.cert.trim() === '' && item.cert_number) cleared.push('cert_number');
    if (form.acquired === '' && item.acquired_on) cleared.push('acquired_on');
    if (form.cost.trim() === '' && item.cost_basis) cleared.push('cost_basis');
    if (raw && item.grading_company) cleared.push('grading_company');

    onSave(item.id, {
      grade_label: form.grade || 'Ungraded',
      grading_company: raw ? null : form.company,
      cert_number: form.cert.trim() || null,
      acquired_on: form.acquired || null,
      quantity: Number.isFinite(count) && count >= 1 ? Math.floor(count) : 1,
      cost_basis: form.cost.trim() || null,
      notes: form.notes,
      cleared,
    });
  };

  const figures = (
    <Stack spacing={1.5} sx={{ minWidth: 0, height: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={3}
        sx={{ minWidth: 0, flexShrink: 0 }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <CollectionTile card={fromHolding(item)} width={TILE_WIDTH} />
        </Box>

        <Stack spacing={2} sx={{ flex: 1, minWidth: 0, justifyContent: 'center' }}>
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            }}
          >
            <Figure
              label="Worth now"
              value={item.holding_value === null ? 'unvalued' : money(item.holding_value)}
              hint={item.quantity > 1 ? `${item.quantity} copies` : 'one copy'}
            />
            <Figure
              label="Paid"
              value={item.holding_cost === null ? 'not recorded' : money(item.holding_cost)}
              hint={item.acquired_on ?? 'date unknown'}
            />
            <Figure
              label="Gain"
              value={gain === null ? '-' : money(item.gain)}
              hint={gain === null ? 'cost not recorded' : gain >= 0 ? 'up' : 'down'}
            />
            <Figure
              label={item.is_graded ? (item.grading_company ?? 'Graded') : 'Raw'}
              value={item.is_graded ? (item.grade_label ?? '-') : 'ungraded'}
              hint={item.cert_number ? `cert ${item.cert_number}` : 'no cert recorded'}
            />
          </Box>
        </Stack>
      </Stack>

      <Box sx={{ flexShrink: 0 }}>
        <HoldingTagPicker item={item} tags={tags} />
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {/* Opens on the grade held, not on whatever sold most. A PSA 10
            holding asked about a PSA 10. */}
        <CardChartArea
          sales={sales}
          loading={detail.isLoading}
          preferredGrade={item.is_graded ? item.grade_label : 'Ungraded'}
        />
      </Box>

      {item.url ? (
        <Stack direction="row" justifyContent="center" sx={{ flexShrink: 0 }}>
          <Link
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            Price page
            <LaunchIcon sx={{ fontSize: 12 }} />
          </Link>
        </Stack>
      ) : null}
    </Stack>
  );

  const details = (
    <HoldingDetailsForm
      form={form}
      onChange={(key, value) => setForm((current) => ({ ...current, [key]: value }))}
    />
  );

  return (
    <CardDialogShell
      open
      tab={tab}
      onTab={setTab}
      onClose={onClose}
      title={
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <span>{item.name}</span>
          <Chip
            size="small"
            variant="outlined"
            label={item.category === 'SPORTS' ? 'Sports' : 'Trading card'}
          />
          {item.is_graded ? (
            <Chip
              size="small"
              variant="outlined"
              label={`${item.grading_company ?? 'Graded'} ${item.grade_label ?? ''}`.trim()}
            />
          ) : null}
        </Stack>
      }
      tabs={[
        { label: 'Figures', content: figures },
        {
          label: sales.length > 0 ? `Sales (${sales.length})` : 'Sales',
          content: (
            <CardSalesTab
              grades={card?.grades ?? []}
              sales={sales}
              sourceUrl={item.url || null}
              loading={detail.isLoading}
              preferredGrade={item.is_graded ? item.grade_label : 'Ungraded'}
            />
          ),
        },
        { label: 'My details', content: details },
      ]}
      actions={
        <>
          <Button size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={save} disabled={busy}>
            Save
          </Button>
        </>
      }
    />
  );
}
